import { useState } from 'react'
import { toast } from 'react-toastify'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@clientbase/clientbase-library'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  Box,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import copy from 'copy-to-clipboard'
import { format } from 'date-fns'

import IconContainer from 'components/IconContainer/IconContainer'
import ModalPaymentLinkDetail from 'components/Modal/ModalPaymentLinkDetail'
import { Table } from 'components/Table'

import { formatMoney } from 'utils'

import { theme } from 'styles/theme'

import { useGetCoupon } from '../../api/useGetCoupon'
import { Coupon, CouponStatus, CouponUsage } from '../../types/coupon'
import { formatCouponPercent } from '../../utils/formatCouponPercent'
import { usosLabel } from '../../utils/usosLabel'
import CopyLinkPopover from '../CopyLinkPopover/CopyLinkPopover'
import CouponActionDialog, { CouponAction } from '../CouponActionDialog'
import * as S from './CouponDetailsModal.styles'

interface CouponDetailsModalProps {
  open: boolean
  handleClose: () => void
  coupon: Coupon | null
  onEdit: (coupon: Coupon) => void
  onDuplicate: (coupon: Coupon) => void
  onPause: (coupon: Coupon) => void | Promise<unknown>
  onUnpause: (coupon: Coupon) => void | Promise<unknown>
  onFinish: (coupon: Coupon) => void | Promise<unknown>
  onReactivate: (coupon: Coupon) => void | Promise<unknown>
  onExtend: (coupon: Coupon) => void
  onDelete: (coupon: Coupon) => void
}

const STATUS_LABELS: Record<CouponStatus, string> = {
  active: 'Ativo',
  paused: 'Pausado',
  expired: 'Expirado',
  exhausted: 'Esgotado',
  finished: 'Finalizado'
}

const STATUS_COLORS: Record<CouponStatus, string> = {
  active: '#4caf50',
  paused: '#ff9800',
  expired: '#9e9e9e',
  exhausted: '#f44336',
  finished: '#616161'
}

const formatCurrency = (value: number | string): string => {
  return `R$${Number(value)
    .toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`
}

const formatQuota = (usageCount: number, maxUsage: number | null): string => {
  if (maxUsage) {
    return `${usageCount} / ${maxUsage} ${usosLabel(maxUsage)}`
  }
  return `${usageCount} ${usosLabel(usageCount)}`
}

const formatUsableType = (type: string): string => {
  switch (type) {
    case 'Billing':
      return 'Cobranca'
    case 'Recurrence':
      return 'Recorrencia'
    default:
      return type
  }
}

const formatDate = (dateStr: string): string => {
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy')
  } catch {
    return dateStr
  }
}

const FREQUENCY_LABELS: Record<string, string> = {
  weekly: 'Semanal',
  biweekly: 'Quinzenal',
  monthly: 'Mensal',
  bimonthly: 'Bimestral',
  quarterly: 'Trimestral',
  semester: 'Semestral',
  annual: 'Anual',
  custom: 'Personalizada'
}

const CouponDetailsModal = ({
  open,
  handleClose,
  coupon,
  onEdit,
  onDuplicate,
  onPause,
  onUnpause,
  onFinish,
  onReactivate,
  onExtend,
  onDelete
}: CouponDetailsModalProps) => {
  const [linkDetailUuid, setLinkDetailUuid] = useState<string | null>(null)

  const openPaymentLinkDetail = (uuid: string) => {
    setLinkDetailUuid(uuid)
  }

  const closeLinkDetail = () => {
    setLinkDetailUuid(null)
  }

  const {
    coupon: rawDetail,
    loading,
    refetch
  } = useGetCoupon(open && coupon ? coupon.uuid : null)

  const detail = rawDetail
    ? {
        ...rawDetail,
        metrics: rawDetail.metrics ?? {
          total_discount_amount: 0,
          total_revenue: 0
        },
        product_links: rawDetail.product_links ?? [],
        coupon_usages: rawDetail.coupon_usages ?? []
      }
    : null

  const status = detail?.status
  const isActive = status === 'active'
  const isPaused = status === 'paused'
  const isFinished = status === 'finished'
  const isExpired = status === 'expired'
  const isExhausted = status === 'exhausted'
  const canExtend = isExpired || isExhausted
  const hasNeverBeenUsed = detail?.usage_count === 0

  const [actionDialog, setActionDialog] = useState<{
    open: boolean
    action: CouponAction | null
  }>({ open: false, action: null })
  const [actionLoading, setActionLoading] = useState(false)

  const requestAction = (action: CouponAction) => {
    if (!detail) return
    setActionDialog({ open: true, action })
  }

  const closeActionDialog = () => {
    setActionDialog({ open: false, action: null })
  }

  const handleActionConfirm = async () => {
    if (!detail || !actionDialog.action) return
    const action = actionDialog.action
    setActionLoading(true)
    try {
      if (action === 'delete') {
        closeActionDialog()
        handleClose()
        onDelete(detail)
        return
      }
      const fn =
        action === 'pause'
          ? onPause
          : action === 'unpause'
          ? onUnpause
          : action === 'finish'
          ? onFinish
          : onReactivate
      try {
        await fn(detail)
      } finally {
        refetch()
      }
    } finally {
      setActionLoading(false)
      closeActionDialog()
    }
  }

  const withCoupon = (fn: (c: Coupon) => void | Promise<unknown>) => () => {
    if (!detail) return
    handleClose()
    fn(detail)
  }

  return (
    <>
      <Modal
        handleClose={handleClose}
        open={open}
        width="100%"
        maxWidth="820px"
        maxHeight="90vh"
        height="auto"
      >
        <ModalBody>
          <ModalHeader onClose={handleClose}>
            <Stack
              direction="row"
              gap={2}
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Stack direction="row" gap={2} alignItems="center">
                <IconContainer icon="faTicket" />
                <Typography variant="mdBold">Detalhes do cupom</Typography>
              </Stack>
              {loading || !detail ? (
                <Skeleton
                  variant="rounded"
                  width={80}
                  height={24}
                  sx={{ mr: 1, borderRadius: '16px' }}
                />
              ) : (
                <Chip
                  label={STATUS_LABELS[detail.status]}
                  size="small"
                  sx={{
                    bgcolor: `${STATUS_COLORS[detail.status]}18`,
                    color: STATUS_COLORS[detail.status],
                    fontWeight: 600,
                    fontSize: '12px',
                    height: 24,
                    mr: 1
                  }}
                />
              )}
            </Stack>
          </ModalHeader>

          <ModalContent>
            {loading || !detail ? (
              <Stack spacing={3}>
                {/* Header: code + discount */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                  flexWrap="wrap"
                >
                  <Skeleton variant="text" width={180} height={32} />
                  <Skeleton variant="text" width={200} height={28} />
                </Stack>

                {/* Metrics */}
                <S.MetricsGrid>
                  {[0, 1, 2].map((i) => (
                    <S.MetricCard key={i}>
                      <Skeleton variant="text" width="60%" height={16} />
                      <Skeleton variant="text" width="80%" height={22} />
                    </S.MetricCard>
                  ))}
                </S.MetricsGrid>

                {/* Info rows */}
                <S.InfoBox>
                  {[140, 160, 150, 130, 150, 170, 110].map((labelW, i) => (
                    <S.InfoRow key={i}>
                      <Skeleton variant="text" width={labelW} height={18} />
                      <S.InfoSeparator />
                      <Skeleton variant="text" width={90} height={18} />
                    </S.InfoRow>
                  ))}
                </S.InfoBox>

                {/* Accordions placeholders */}
                <Stack spacing={1}>
                  {[0, 1].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        border: '1px solid #E8EBE8',
                        borderLeft: '3px solid',
                        borderLeftColor: 'primary.main',
                        borderRadius: '8px',
                        backgroundColor: '#FAFBFA',
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <Skeleton variant="rounded" width={35} height={35} />
                      <Skeleton variant="text" width={140} height={20} />
                    </Box>
                  ))}
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={3}>
                {/* Header: code + discount */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                  flexWrap="wrap"
                >
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="lgBold">{detail.code}</Typography>
                    <Tooltip title="Copiar código" arrow>
                      <IconButton
                        onClick={() => {
                          copy(detail.code)
                          toast.success('Código copiado')
                        }}
                      >
                        <ContentCopyIcon
                          sx={{ fontSize: 20, color: 'neutral.400' }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Typography
                    sx={{
                      fontSize: '22px',
                      fontWeight: 700,
                      color: 'neutral.100',
                      lineHeight: 1.2
                    }}
                  >
                    {detail.discount_type === 'percentage'
                      ? formatCouponPercent(detail.discount_value)
                      : formatCurrency(detail.discount_value)}
                    {detail.discount_type === 'percentage' &&
                      detail.max_discount &&
                      ` (max ${formatCurrency(detail.max_discount)})`}
                  </Typography>
                </Stack>

                {/* Metrics */}
                <S.MetricsGrid>
                  <S.MetricCard>
                    <S.MetricTitle>Usos</S.MetricTitle>
                    <S.MetricValue>
                      {formatQuota(detail.usage_count, detail.max_usage)}
                    </S.MetricValue>
                  </S.MetricCard>

                  <S.MetricCard>
                    <S.MetricTitle>Desconto concedido</S.MetricTitle>
                    <S.MetricValue>
                      {formatCurrency(detail.metrics.total_discount_amount)}
                    </S.MetricValue>
                  </S.MetricCard>

                  <S.MetricCard>
                    <S.MetricTitle>Receita gerada</S.MetricTitle>
                    <S.MetricValue>
                      {formatCurrency(detail.metrics.total_revenue)}
                    </S.MetricValue>
                  </S.MetricCard>
                </S.MetricsGrid>

                {/* Info rows */}
                <S.InfoBox>
                  <S.InfoRow>
                    <Typography variant="smLight" color="neutral.400">
                      Tipo de desconto
                    </Typography>
                    <S.InfoSeparator />
                    <Typography variant="smBold">
                      {detail.discount_type === 'percentage'
                        ? 'Percentual'
                        : 'Valor fixo'}
                    </Typography>
                  </S.InfoRow>

                  <S.InfoRow>
                    <Typography variant="smLight" color="neutral.400">
                      Valor do desconto
                    </Typography>
                    <S.InfoSeparator />
                    <Typography variant="smBold">
                      {detail.discount_type === 'percentage'
                        ? formatCouponPercent(detail.discount_value)
                        : formatCurrency(detail.discount_value)}
                    </Typography>
                  </S.InfoRow>

                  {detail.discount_type === 'percentage' && (
                    <S.InfoRow>
                      <Typography variant="smLight" color="neutral.400">
                        Desconto máximo
                      </Typography>
                      <S.InfoSeparator />
                      <Typography variant="smBold">
                        {detail.max_discount
                          ? formatCurrency(detail.max_discount)
                          : 'Sem limite'}
                      </Typography>
                    </S.InfoRow>
                  )}

                  <S.InfoRow>
                    <Typography variant="smLight" color="neutral.400">
                      Limite de usos
                    </Typography>
                    <S.InfoSeparator />
                    <Typography variant="smBold">
                      {detail.max_usage
                        ? `${detail.max_usage} ${usosLabel(detail.max_usage)}`
                        : 'Ilimitado'}
                    </Typography>
                  </S.InfoRow>

                  <S.InfoRow>
                    <Typography variant="smLight" color="neutral.400">
                      Usos registrados
                    </Typography>
                    <S.InfoSeparator />
                    <Typography variant="smBold">
                      {formatQuota(detail.usage_count, detail.max_usage)}
                    </Typography>
                  </S.InfoRow>

                  <S.InfoRow>
                    <Typography variant="smLight" color="neutral.400">
                      Uso único por cliente
                    </Typography>
                    <S.InfoSeparator />
                    <Typography variant="smBold">
                      {detail.single_use_per_customer ? 'Sim' : 'Não'}
                    </Typography>
                  </S.InfoRow>

                  <S.InfoRow>
                    <Typography variant="smLight" color="neutral.400">
                      Validade
                    </Typography>
                    <S.InfoSeparator />
                    <Typography variant="smBold">
                      {detail.expires_at
                        ? formatDate(detail.expires_at)
                        : 'Sem validade'}
                    </Typography>
                  </S.InfoRow>
                </S.InfoBox>

                <Stack spacing={1}>
                  {/* Product Links */}
                  <Accordion
                    sx={{
                      border: '1px solid #E8EBE8',
                      borderLeft: '3px solid',
                      borderLeftColor: 'primary.main',
                      borderRadius: '8px !important',
                      backgroundColor: '#FAFBFA',
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        backgroundColor: '#fff'
                      }
                    }}
                  >
                    <AccordionSummary
                      aria-controls="coupon-links-content"
                      id="coupon-links"
                    >
                      <Box display="flex" alignItems="center" gap="8px">
                        <Box
                          sx={{
                            width: 35,
                            height: 35,
                            background: '#F8F8F8',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                        >
                          <Icon
                            icon="link"
                            colorSX={theme.palette.primary.main}
                          />
                        </Box>
                        <Typography variant="smLight">
                          Links associados
                        </Typography>
                        {detail.product_links.length > 0 && (
                          <Chip
                            label={detail.product_links.length}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '11px',
                              fontWeight: 600
                            }}
                          />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {detail.product_links.length === 0 ? (
                        <Typography
                          variant="smLight"
                          color="neutral.400"
                          py={1}
                        >
                          Nenhum link associado
                        </Typography>
                      ) : (
                        <Table
                          mobileCards
                          header={[
                            { id: 'name', label: 'Nome' },
                            { id: 'type', label: 'Tipo' },
                            { id: 'amount', label: 'Valor' },
                            { id: 'usage', label: 'Usos' },
                            { id: 'action', label: '' }
                          ]}
                          rows={detail.product_links.map((link) => {
                            const amountNum = Number(link.amount ?? 0)
                            const frequencyLabel =
                              link.frequency && FREQUENCY_LABELS[link.frequency]
                            const typeLabel =
                              link.charge_type === 'recurrent' && frequencyLabel
                                ? `Recorrente (${frequencyLabel})`
                                : link.charge_type === 'recurrent'
                                ? 'Recorrente'
                                : link.charge_type === 'single'
                                ? 'Único'
                                : '-'
                            return {
                              name: link.name,
                              type: typeLabel,
                              amount: formatMoney(amountNum),
                              usage: link.usage_count ?? 0,
                              action: {
                                component: (
                                  <Stack direction="row" gap={0.5}>
                                    <CopyLinkPopover
                                      linkUrl={link.slug}
                                      coupons={[
                                        { uuid: detail.uuid, code: detail.code }
                                      ]}
                                    >
                                      {({ onClick }) => (
                                        <Tooltip title="Copiar link" arrow>
                                          <IconButton onClick={onClick}>
                                            <ContentCopyIcon
                                              sx={{
                                                fontSize: 20,
                                                color: 'neutral.400'
                                              }}
                                            />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </CopyLinkPopover>
                                    <Tooltip title="Abrir detalhes" arrow>
                                      <IconButton
                                        onClick={() =>
                                          openPaymentLinkDetail(link.uuid)
                                        }
                                      >
                                        <Icon
                                          icon="settings"
                                          sx={{ fontSize: '20px' }}
                                          colorSX={theme.palette.neutral[400]}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                )
                              }
                            }
                          })}
                          select={{ useSelect: false }}
                          hiddePagination
                        />
                      )}
                    </AccordionDetails>
                  </Accordion>

                  {/* Usage History */}
                  <Accordion
                    sx={{
                      border: '1px solid #E8EBE8',
                      borderLeft: '3px solid',
                      borderLeftColor: 'primary.main',
                      borderRadius: '8px !important',
                      backgroundColor: '#FAFBFA',
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        backgroundColor: '#fff'
                      }
                    }}
                  >
                    <AccordionSummary
                      aria-controls="coupon-usages-content"
                      id="coupon-usages"
                    >
                      <Box display="flex" alignItems="center" gap="8px">
                        <Box
                          sx={{
                            width: 35,
                            height: 35,
                            background: '#F8F8F8',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                        >
                          <Icon
                            icon="article"
                            colorSX={theme.palette.primary.main}
                          />
                        </Box>
                        <Typography variant="smLight">
                          Histórico de usos
                        </Typography>
                        {detail.coupon_usages.length > 0 && (
                          <Chip
                            label={detail.coupon_usages.length}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '11px',
                              fontWeight: 600
                            }}
                          />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {detail.coupon_usages.length === 0 ? (
                        <Typography
                          variant="smLight"
                          color="neutral.400"
                          py={1}
                        >
                          Nenhum uso registrado
                        </Typography>
                      ) : (
                        <Table
                          mobileCards
                          header={[
                            { id: 'customer', label: 'Cliente' },
                            { id: 'resource', label: 'Recurso' },
                            { id: 'date', label: 'Data' },
                            { id: 'removed', label: 'Removido' },
                            { id: 'discount', label: 'Desconto' },
                            { id: 'action', label: '' }
                          ]}
                          rows={detail.coupon_usages.map(
                            (usage: CouponUsage) => ({
                              customer: {
                                component: (
                                  <Typography variant="smBold">
                                    {usage.customer.name}
                                  </Typography>
                                )
                              },
                              resource: formatUsableType(usage.usable_type),
                              date: formatDate(usage.created_at),
                              removed: {
                                component: usage.removed_at ? (
                                  <S.RemovedBadge>
                                    {formatDate(usage.removed_at)}
                                  </S.RemovedBadge>
                                ) : (
                                  <Typography
                                    variant="smLight"
                                    color="neutral.400"
                                  >
                                    -
                                  </Typography>
                                )
                              },
                              discount: formatCurrency(
                                usage.applied_discount_amount
                              ),
                              action: {
                                component: (
                                  <Tooltip title="Abrir cliente" arrow>
                                    <IconButton
                                      onClick={() =>
                                        window.open(
                                          `/clientes/${usage.customer.uuid}`,
                                          '_blank'
                                        )
                                      }
                                    >
                                      <Icon
                                        icon="open"
                                        sx={{ fontSize: '20px' }}
                                        colorSX={theme.palette.neutral[400]}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                )
                              }
                            })
                          )}
                          select={{ useSelect: false }}
                          hiddePagination
                        />
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Stack>
              </Stack>
            )}
          </ModalContent>

          {loading || !detail ? (
            <ModalFooter>
              <Stack
                direction="row"
                gap={1}
                flexWrap="wrap"
                justifyContent="flex-end"
                width="100%"
              >
                <Skeleton variant="rounded" width={88} height={40} />
                <Skeleton variant="rounded" width={88} height={40} />
                <Skeleton variant="rounded" width={88} height={40} />
                <Skeleton variant="rounded" width={100} height={40} />
              </Stack>
            </ModalFooter>
          ) : (
            <ModalFooter>
              <Stack
                direction="row"
                gap={1}
                flexWrap="wrap"
                justifyContent="flex-end"
                width="100%"
              >
                {hasNeverBeenUsed ? (
                  <Button
                    variantButton="neutral"
                    onClick={() => requestAction('delete')}
                    type="button"
                  >
                    Excluir
                  </Button>
                ) : (
                  <Tooltip
                    title="Este cupom não pode ser excluído pois já foi utilizado"
                    arrow
                  >
                    <span>
                      <Button variantButton="neutral" disabled type="button">
                        Excluir
                      </Button>
                    </span>
                  </Tooltip>
                )}

                <Button
                  variantButton="neutral"
                  onClick={() => detail && onDuplicate(detail)}
                  type="button"
                >
                  Duplicar
                </Button>

                <Button
                  variantButton="neutral"
                  onClick={withCoupon(onEdit)}
                  type="button"
                >
                  Editar
                </Button>

                {isActive && (
                  <Button
                    variantButton="neutral"
                    onClick={() => requestAction('pause')}
                    type="button"
                  >
                    Pausar
                  </Button>
                )}

                {isPaused && (
                  <Button
                    variantButton="neutral"
                    onClick={() => requestAction('unpause')}
                    type="button"
                  >
                    Despausar
                  </Button>
                )}

                {isActive && (
                  <Button
                    variantButton="primary"
                    onClick={() => requestAction('finish')}
                    type="button"
                  >
                    Finalizar
                  </Button>
                )}

                {isFinished && (
                  <Button
                    variantButton="primary"
                    onClick={() => requestAction('reactivate')}
                    type="button"
                  >
                    Reativar
                  </Button>
                )}

                {canExtend && (
                  <Button
                    variantButton="primary"
                    onClick={withCoupon(onExtend)}
                    type="button"
                  >
                    Estender
                  </Button>
                )}
              </Stack>
            </ModalFooter>
          )}
        </ModalBody>
      </Modal>
      {linkDetailUuid && (
        <ModalPaymentLinkDetail
          isOpenModalPaymentDetail={!!linkDetailUuid}
          handleCloseModalDetailPayment={closeLinkDetail}
          productLink={{ uuid: linkDetailUuid } as any}
        />
      )}
      <CouponActionDialog
        open={actionDialog.open}
        action={actionDialog.action}
        coupon={detail}
        loading={actionLoading}
        onClose={closeActionDialog}
        onConfirm={handleActionConfirm}
      />
    </>
  )
}

export default CouponDetailsModal
